<template>
    <nav class="tab-nav-container" :class="{ 'nav-top': navTop, 'pill-size-large': pillSize === 'large' }" aria-label="Tab Navigation">
        <!-- Left Arrow Button -->
        <button v-if="showLeftArrow" class="arrow arrow-left" @click="scrollLeft" aria-label="Scroll left">
            <SvgIcon name="forward" class="arrow-icon left" />
        </button>

        <!-- Tabs List -->
        <ul ref="tabsList" class="tabs-list" role="tablist">
            <li
                v-if="logo"
                class="logo-holder"
                :class="{ 'is-visible': navTop }"
                :aria-hidden="!navTop"
                @click="navTop ? selectFirstTab() : null"
            >
                <LocationLogo :logo="logo" :logoName="logoName" :shape="shape" />
            </li>
            <li v-for="tab in tabs" :key="tab.ref" :data-tab-ref="tab.ref" :class="{ 'is-selected': isSelected(tab) }" @click="selectTab(tab)"
                role="tab" :aria-selected="isSelected(tab)">
                <div class="tab-content">
                    <SvgIcon name="reorder" class="reorder-icon" v-if="tab.name === 'Order Again'" />
                    {{ tab.name }}
                </div>
            </li>
        </ul>

        <!-- Right Arrow Button -->
        <button v-if="showRightArrow" class="arrow arrow-right" @click="scrollRight" aria-label="Scroll right">
            <SvgIcon name="forward" class="arrow-icon right" />
        </button>
    </nav>
</template>

<script>
import debounce from 'lodash/debounce';
import SvgIcon from './SvgIcon.vue';
import LocationLogo from './LocationLogo.vue';
import { interactionSound } from '@/mixins/interactionSound';

export default {
    name: 'TabNav',
    mixins: [interactionSound],
    components: { SvgIcon, LocationLogo },
    props: {
        tabs: Array,
        logo: String,
        logoName: String,
        shape: String,
        selectedTabRef: String,
        navTop: Boolean,
        pillSize: {
            type: String,
            default: 'normal',
            validator: (v) => ['normal', 'large'].includes(v),
        },
    },
    data() {
        return {
            showLeftArrow: false,
            showRightArrow: false,
            debouncedCheck: null,
            isUserScrolling: false,
            userScrollTimeout: null,
            isAutoScrolling: false,
            selectedScrollRaf: null,
        };
    },
    watch: {
        selectedTabRef() {
            // Keep the selected tab visible when selection changes (e.g., content scroll)
            this.queueScrollSelectedIntoView({ mode: 'nearest' });
        },
        tabs() {
            // Tabs can change after data loads; re-check arrows + keep selection visible.
            this.$nextTick(() => {
                this.checkScrollArrows();
                this.queueScrollSelectedIntoView({ mode: 'nearest' });
            });
        },
    },
    mounted() {
        this.debouncedCheck = debounce(this.checkScrollArrows, 200);
        this.$nextTick(() => {
            this.checkScrollArrows();
            if (this.$refs.tabsList) {
                this.$refs.tabsList.addEventListener('scroll', this.onTabsScroll, { passive: true });
            }
        });
        window.addEventListener('resize', this.debouncedCheck);
    },
    unmounted() {
        window.removeEventListener('resize', this.debouncedCheck);
        if (this.$refs.tabsList) {
            this.$refs.tabsList.removeEventListener('scroll', this.onTabsScroll);
        }
        if (this.userScrollTimeout) {
            clearTimeout(this.userScrollTimeout);
            this.userScrollTimeout = null;
        }
        if (this.selectedScrollRaf) {
            cancelAnimationFrame(this.selectedScrollRaf);
            this.selectedScrollRaf = null;
        }
    },
    methods: {
        queueScrollSelectedIntoView({ mode = 'nearest' } = {}) {
            if (this.selectedScrollRaf) return;
            this.selectedScrollRaf = requestAnimationFrame(() => {
                this.selectedScrollRaf = null;
                this.scrollSelectedIntoView({ mode });
            });
        },
        onTabsScroll() {
            // Track user-driven scroll so we don't fight their gesture.
            if (!this.isAutoScrolling) {
                this.isUserScrolling = true;
                if (this.userScrollTimeout) clearTimeout(this.userScrollTimeout);
                this.userScrollTimeout = setTimeout(() => {
                    this.isUserScrolling = false;
                }, 180);
            }

            if (this.debouncedCheck) {
                this.debouncedCheck();
            } else {
                this.checkScrollArrows();
            }
        },
        selectFirstTab() {
            this.playInteractionSound();
            this.scrollToStart();
            this.$emit('tab-selected', this.tabs[0].ref);
        },
        selectTab(tab) {
            this.playInteractionSound();
            // Click selection: scroll it more intentionally so it feels responsive.
            this.scrollTabIntoView(tab.ref, { mode: tab.ref === this.tabs?.[0]?.ref ? 'start' : 'center' });
            this.$emit('tab-selected', tab.ref);
        },
        isSelected(tab) {
            return this.selectedTabRef === tab.ref;
        },
        checkScrollArrows() {
            const el = this.$refs.tabsList;
            if (!el) return;

            const tolerance = 5; // Tolerance for floating point inaccuracies
            this.showLeftArrow = el.scrollLeft > tolerance;
            this.showRightArrow =
                el.scrollLeft + el.clientWidth < el.scrollWidth - tolerance;
        },
        scrollLeft() {
            this.playInteractionSound();
            this.scrollTabs(-1);
        },
        scrollRight() {
            this.playInteractionSound();
            this.scrollTabs(1);
        },
        scrollTabs(direction) {
            const el = this.$refs.tabsList;
            const scrollAmount = 160; // 10rem in pixels

            if (direction > 0) {
                // Scroll Right
                this.isAutoScrolling = true;
                el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            } else {
                // Scroll Left
                this.isAutoScrolling = true;
                el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            }

            setTimeout(() => {
                this.isAutoScrolling = false;
            }, 250);

            this.$nextTick(() => this.checkScrollArrows());
        },
        scrollToStart() {
            const el = this.$refs.tabsList;
            if (!el) return;
            this.isAutoScrolling = true;
            el.scrollTo({ left: 0, behavior: 'smooth' });
            setTimeout(() => {
                this.isAutoScrolling = false;
            }, 250);
            this.$nextTick(() => this.checkScrollArrows());
        },
        getTabElementByRef(tabRef) {
            const el = this.$refs.tabsList;
            if (!el || !tabRef) return null;
            const esc = (typeof CSS !== 'undefined' && CSS.escape)
                ? CSS.escape(tabRef)
                : String(tabRef).replace(/"/g, '\\"');
            return el.querySelector(`[data-tab-ref="${esc}"]`);
        },
        scrollTabIntoView(tabRef, { mode = 'nearest' } = {}) {
            const el = this.$refs.tabsList;
            if (!el || !tabRef) return;
            if (this.isUserScrolling) return;

            const tabEl = this.getTabElementByRef(tabRef);
            if (!tabEl) return;

            const containerRect = el.getBoundingClientRect();
            const tabRect = tabEl.getBoundingClientRect();

            const padding = 20; // a little breathing room so it doesn't feel tight to the edge
            const currentLeft = el.scrollLeft;

            let targetLeft = null;

            if (mode === 'start') {
                targetLeft = 0;
            } else if (mode === 'center') {
                // Center the tab within the visible area
                const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2;
                targetLeft = tabCenter - el.clientWidth / 2;
            } else {
                // 'nearest': only move enough to fully reveal the pill
                const overflowLeft = containerRect.left - tabRect.left + padding;
                const overflowRight = tabRect.right - containerRect.right + padding;

                if (overflowLeft > 0) {
                    targetLeft = currentLeft - overflowLeft;
                } else if (overflowRight > 0) {
                    targetLeft = currentLeft + overflowRight;
                } else {
                    // Fully visible; do nothing
                    return;
                }
            }

            // Clamp to bounds
            const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
            const clamped = Math.min(maxLeft, Math.max(0, targetLeft));

            this.isAutoScrolling = true;
            el.scrollTo({ left: clamped, behavior: 'smooth' });
            setTimeout(() => {
                this.isAutoScrolling = false;
            }, 250);

            this.$nextTick(() => this.checkScrollArrows());
        },
        scrollSelectedIntoView({ mode = 'nearest' } = {}) {
            const el = this.$refs.tabsList;
            if (!el) return;
            if (!this.selectedTabRef) return;
            this.scrollTabIntoView(this.selectedTabRef, { mode });
        },
    },
};
</script>


<style scoped>
.tab-nav-container {
    position: sticky;
    top: 0px;
    display: flex;
    align-items: center;
    background-color: var(--grey-50);
    padding-top: 1.2rem;
    padding-bottom: 1.2rem;
    z-index: 3000;
    transition: background-color 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}

.nav-top.tab-nav-container {
    padding-top: 1.2rem;
    padding-bottom: 1.2rem;
    border-bottom: 0.125rem solid var(--grey-outline);
    box-shadow: var(--box-shadow);
    background-color: var(--primary-white);
}

.tabs-list {
    list-style: none;
    padding: 0;
    padding-left: 2.5rem;
    margin: 0;
    display: flex;
    overflow-x: auto;
    flex-grow: 1;
    white-space: nowrap;
    scrollbar-width: none;
    -ms-overflow-style: none;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x proximity;
}

.tabs-list::-webkit-scrollbar {
    display: none;
}

li {
    cursor: pointer;
    font-size: 1.625rem;
    line-height: 2.21625rem;
    color: var(--primary-black);
    font-weight: normal;
    scroll-snap-align: center;
}

li.logo-holder {
    background-color: transparent !important;
    width: 0;
    height: 3.5rem;
    overflow: hidden;
    flex: 0 0 auto;
    margin-right: 0;
    opacity: 0;
    transform: translateX(-0.5rem) scale(0.98);
    transition: width 180ms ease, margin-right 180ms ease, opacity 180ms ease, transform 180ms ease;
    pointer-events: none;
    will-change: width, opacity, transform;
}

.logo-holder.is-visible {
    width: 3.5rem;
    margin-right: 1rem;
    opacity: 1;
    transform: translateX(0) scale(1);
    pointer-events: auto;
}

.logo-holder .logo-container {
    width: 3.5rem;
    height: 3.5rem;
    margin-left: 0;
    margin-right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* NOTE: keep leading spacing stable regardless of whether the logo pill is rendered */

.tab-content {
    background-color: var(--grey-pressed);
    border-radius: 6.25rem;
    margin-right: 1rem;
    padding: 0.625rem 2.0625rem;
}

.tab-nav-container.pill-size-large li {
    font-size: 2rem;
    line-height: 2.75rem;
}

.tab-nav-container.pill-size-large .tab-content {
    padding: 0.875rem 2.5rem;
}

.tab-nav-container.pill-size-large .logo-holder {
    height: 3.85rem;
}

.tab-nav-container.pill-size-large .logo-holder.is-visible {
    width: 3.85rem;
    margin-right: 1rem;
}

.tab-nav-container.pill-size-large .logo-holder .logo-container {
    width: 3.85rem;
    height: 3.85rem;
}

li.is-selected .tab-content {
    background-color: var(--primary-black);
    color: var(--primary-white);
}

.arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 100;
    border: none;
    cursor: pointer;
    background-color: transparent;
    width: auto;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem;
    width: 3.25rem;
    height: 3.25rem;
    color: var(--primary-white);
    fill: var(--primary-white);
    background-color: var(--primary-black);
    border-radius: 6.25rem;
    margin-right: 1rem;
}

.arrow-icon {
    fill: var(--primary-white) !important;
    width: 1.5rem;
    height: 1.5rem;
}

.tab-nav-container.pill-size-large .arrow {
    width: 3.5rem;
    height: 3.5rem;
}

.tab-nav-container.pill-size-large .arrow-icon {
    width: 1.625rem;
    height: 1.625rem;
}

.arrow-icon.left {
    transform: rotate(180deg);
}

.arrow-left {
    left: 1rem;
}

.arrow-right {
    right: 0;
}
</style>
